/* ─── SVG Scene: declarative shapes with optional animations ─── */

import { useCallback, useEffect, useRef } from 'react';

import { useArtifactSendEvent } from '../store';

// ─── Types ───────────────────────────────────────────────────────────

interface SvgShape {
  type: string;
  children?: Record<string, SvgShape> | SvgShape[];
  transition?: string;
  [key: string]: unknown;
}

type ShapeMap = Record<string, SvgShape> | SvgShape[];

// ─── Math shorthands for animation expressions ──────────────────────

const MATH_SHORTHANDS: Record<string, string> = {
  cos: 'Math.cos',
  sin: 'Math.sin',
  tan: 'Math.tan',
  abs: 'Math.abs',
  sqrt: 'Math.sqrt',
  pow: 'Math.pow',
  min: 'Math.min',
  max: 'Math.max',
  PI: 'Math.PI',
  floor: 'Math.floor',
  ceil: 'Math.ceil',
  round: 'Math.round',
  random: 'Math.random',
};

function expandMathShorthands(expr: string): string {
  let result = expr;
  for (const [short, full] of Object.entries(MATH_SHORTHANDS)) {
    result = result.replace(new RegExp(`\\b${short}\\b`, 'g'), full);
  }
  return result;
}

// ─── Shape rendering ────────────────────────────────────────────────

function pointsToString(points: [number, number][]): string {
  return points.map(([x, y]) => `${x},${y}`).join(' ');
}

function renderShape(
  shape: SvgShape,
  id: string,
  onClick: (shapeId: string, x: number, y: number) => void,
): JSX.Element | null {
  const style: React.CSSProperties = {};
  if (shape.opacity != null && shape.opacity !== 1) style.opacity = shape.opacity as number;
  if (shape.transition) style.transition = shape.transition as string;

  const common = {
    'data-shape-id': id,
    style: Object.keys(style).length > 0 ? style : undefined,
    onClick: (e: React.MouseEvent<SVGElement>) => {
      e.stopPropagation();
      const svg = (e.target as SVGElement).closest('svg');
      if (!svg) return;
      const pt = svg.createSVGPoint();
      pt.x = e.clientX;
      pt.y = e.clientY;
      const ctm = svg.getScreenCTM();
      if (ctm) {
        const svgPt = pt.matrixTransform(ctm.inverse());
        onClick(id, svgPt.x, svgPt.y);
      }
    },
    fill: (shape.fill as string) || undefined,
    stroke: (shape.stroke as string) || undefined,
    strokeWidth: (shape.stroke_width as number) || undefined,
  };

  switch (shape.type) {
    case 'circle':
      return <circle key={id} cx={shape.cx as number} cy={shape.cy as number} r={shape.r as number} {...common} />;

    case 'ellipse':
      return <ellipse key={id} cx={shape.cx as number} cy={shape.cy as number} rx={shape.rx as number} ry={shape.ry as number} {...common} />;

    case 'rect':
      return <rect key={id} x={shape.x as number} y={shape.y as number} width={shape.width as number} height={shape.height as number} rx={shape.rx as number} {...common} />;

    case 'line':
      return <line key={id} x1={shape.x1 as number} y1={shape.y1 as number} x2={shape.x2 as number} y2={shape.y2 as number} {...common} />;

    case 'polyline':
      return <polyline key={id} points={pointsToString(shape.points as [number, number][])} {...common} />;

    case 'polygon':
      return <polygon key={id} points={pointsToString(shape.points as [number, number][])} {...common} />;

    case 'text':
      return (
        <text key={id} x={shape.x as number} y={shape.y as number}
          fontSize={shape.font_size as number} fontFamily={shape.font_family as string}
          textAnchor={shape.text_anchor as 'start' | 'middle' | 'end' | 'inherit'} {...common}>
          {shape.text as string}
        </text>
      );

    case 'path':
      return <path key={id} d={shape.d as string} {...common} />;

    case 'image':
      return <image key={id} href={shape.href as string} x={shape.x as number} y={shape.y as number} width={shape.width as number} height={shape.height as number} style={style} />;

    case 'group': {
      const tx = shape.translate as [number, number] | undefined;
      const rot = shape.rotate as number | undefined;
      const sc = shape.scale as number | [number, number] | undefined;
      const parts: string[] = [];
      if (tx && (tx[0] || tx[1])) parts.push(`translate(${tx[0]}, ${tx[1]})`);
      if (rot) parts.push(`rotate(${rot})`);
      if (sc != null && sc !== 1) {
        if (Array.isArray(sc)) parts.push(`scale(${sc[0]}, ${sc[1]})`);
        else parts.push(`scale(${sc})`);
      }
      const transform = parts.length > 0 ? parts.join(' ') : undefined;
      const children = shape.children;
      return (
        <g key={id} transform={transform} style={style}>
          {renderShapes(children as ShapeMap, id, onClick)}
        </g>
      );
    }

    default:
      return null;
  }
}

function renderShapes(
  shapes: ShapeMap | undefined,
  parentId: string,
  onClick: (shapeId: string, x: number, y: number) => void,
): JSX.Element[] {
  if (!shapes) return [];
  const elements: JSX.Element[] = [];
  if (Array.isArray(shapes)) {
    shapes.forEach((shape, i) => {
      const id = parentId ? `${parentId}.${i}` : String(i);
      const el = renderShape(shape, id, onClick);
      if (el) elements.push(el);
    });
  } else {
    for (const [key, shape] of Object.entries(shapes)) {
      const id = parentId ? `${parentId}.${key}` : key;
      const el = renderShape(shape, id, onClick);
      if (el) elements.push(el);
    }
  }
  return elements;
}

// ─── Animation engine ───────────────────────────────────────────────

type AnimationFn = (t: number, el: SVGElement) => void;

function compileAnimation(expr: string): AnimationFn {
  const expanded = expandMathShorthands(expr);
  // Parse "attr = expr; attr2 = expr2; ..." into assignments
  const assignments = expanded.split(';').map((s) => s.trim()).filter(Boolean);
  const body = assignments
    .map((a) => {
      const eq = a.indexOf('=');
      if (eq < 0) return '';
      const attr = a.slice(0, eq).trim();
      const value = a.slice(eq + 1).trim();
      return `el.setAttribute('${attr}', String(${value}));`;
    })
    .join('\n');
  // eslint-disable-next-line no-new-func
  return new Function('t', 'el', body) as AnimationFn;
}

function findElementByShapeId(svg: SVGSVGElement, shapeId: string): SVGElement | null {
  return svg.querySelector(`[data-shape-id="${shapeId}"]`);
}

function collectAnimations(
  shapes: ShapeMap,
  parentId: string,
  svg: SVGSVGElement,
  out: { fn: AnimationFn; el: SVGElement }[],
): void {
  const entries = Array.isArray(shapes)
    ? shapes.map((s, i) => [String(i), s] as const)
    : Object.entries(shapes);
  for (const [key, shape] of entries) {
    const id = parentId ? `${parentId}.${key}` : key;
    const expr = shape.animate as string | undefined;
    if (expr) {
      const el = findElementByShapeId(svg, id);
      if (el) {
        try {
          out.push({ fn: compileAnimation(expr), el });
        } catch (e) {
          console.error(`[SVG] Failed to compile animation for '${id}':`, e);
        }
      }
    }
    if (shape.type === 'group' && shape.children) {
      collectAnimations(shape.children as ShapeMap, id, svg, out);
    }
  }
}

// ─── Component ──────────────────────────────────────────────────────

export function ArtifactSvgScene({
  shapes,
  width = 400,
  height = 300,
  background = 'transparent',
  __node_id,
}: {
  shapes?: ShapeMap;
  width?: number;
  height?: number;
  background?: string;
  __node_id?: string;
}) {
  const sendEvent = useArtifactSendEvent();
  const svgRef = useRef<SVGSVGElement>(null);
  const animRef = useRef<number>(0);

  const handleClick = useCallback(
    (shapeId: string, x: number, y: number) => {
      if (__node_id) {
        sendEvent(__node_id, 'svg_click', { kind: 'SvgShapeClicked', shape_id: shapeId, x, y });
      }
    },
    [__node_id, sendEvent],
  );

  // Animation loop — collect `animate` expressions from shapes
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg || !shapes) return;

    const compiled: { fn: AnimationFn; el: SVGElement }[] = [];
    collectAnimations(shapes, '', svg, compiled);

    if (compiled.length === 0) return;

    const start = performance.now();
    function tick() {
      const t = (performance.now() - start) / 1000;
      for (const { fn, el } of compiled) {
        try {
          fn(t, el);
        } catch {
          // ignore runtime animation errors
        }
      }
      animRef.current = requestAnimationFrame(tick);
    }
    animRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animRef.current);
  }, [shapes]);

  return (
    <svg
      ref={svgRef}
      viewBox={`0 0 ${width} ${height}`}
      style={{ background, width: '100%', maxWidth: width, aspectRatio: `${width} / ${height}` }}
      xmlns="http://www.w3.org/2000/svg"
    >
      {renderShapes(shapes, '', handleClick)}
    </svg>
  );
}
